import { controlGroupClass, controlLookClass } from '@crow/ui/field';
import type { ClassValue } from 'clsx';
import { cn } from './utils';

/**
 * The one surface every popup shares — dialogs, menus, popovers, hover cards,
 * selects, the command palette, toasts and tooltips — so they read as a single
 * family. Radius, padding and size stay with each component.
 */
export const POPUP_SURFACE =
  'border border-hairline-strong bg-desktop-overlay text-foreground shadow-desktop-float';

/** Crowdex's text size in its fields, and the selection in the focus colour. */
const FIELD_TYPE = 'text-ui selection:bg-ring/35';

/**
 * The one look every text field shares — inputs, textareas, input groups and select triggers, on
 * a page or in a popup: the design system's recessed control (`@crow/ui`'s
 * `controlLookClass('recessed')`: the input line, the `field` fill, the accent border and halo on
 * any focus — a field focused by a dialog opening is where typing goes — and the danger ones when
 * invalid), in Crowdex's type size. Add FIELD_GEOMETRY for a single-line control.
 */
export function fieldClass(...classes: ClassValue[]): string {
  return cn(controlLookClass('recessed'), FIELD_TYPE, ...classes);
}

/**
 * A field drawn without frame, fill or focus halo (`@crow/ui`'s `bare` surface): the control
 * inside an input group, whose frame shows its focus, or a title edited in place.
 */
export function bareFieldClass(...classes: ClassValue[]): string {
  return cn(controlLookClass('bare'), FIELD_TYPE, ...classes);
}

/**
 * The frame around a bare control and what sits beside it (an icon, a button): the field's look,
 * its focus and invalid states following the control inside (`@crow/ui`'s `controlGroupClass`).
 */
export function fieldGroupClass(...classes: ClassValue[]): string {
  return cn(controlGroupClass('recessed'), FIELD_TYPE, ...classes);
}

/** The charter's field, at Crowdex's density: 32 px tall at normal density, 6 px corners. */
export const FIELD_GEOMETRY = 'h-control-md rounded-control px-2.5';

/**
 * A chosen chip, segment or filter ("All", "Medium", "A · Bold"): the control surface with the
 * text colour and a hairline — never the filled white of the screen's one primary button.
 * Unchosen siblings stay quiet (muted text on a faint hairline or nothing).
 */
export const SELECTED_CONTROL = 'border-input bg-control text-foreground';
